import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  AlertTitle,
  Collapse
} from '@mui/material';
import {
  IconRefresh as RefreshIcon,
  IconChevronDown as ExpandMoreIcon,
  IconChevronUp as ExpandLessIcon,
  IconBug as BugReportIcon
} from '@tabler/icons-react';

/**
 * Error boundary component to catch and display React errors gracefully
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false
    };
  }

  static getDerivedStateFromError(_error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    this.setState({
      error,
      errorInfo
    });

    // Report error to monitoring service if available
    if (window.reportError) {
      window.reportError(error, errorInfo);
    }
  }

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  toggleDetails = () => {
    this.setState(prevState => ({
      showDetails: !prevState.showDetails
    }));
  };

  render() {
    if (this.state.hasError) {
      const { fallback: Fallback, title, message } = this.props;
      
      // Use custom fallback if provided
      if (Fallback) {
        return (
          <Fallback
            error={this.state.error}
            errorInfo={this.state.errorInfo}
            onRetry={this.handleRetry}
            onReload={this.handleReload}
          />
        );
      }

      // Default error UI
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
          p={3}
        >
          <Card sx={{ maxWidth: 600, width: '100%' }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <Box component="span" sx={{ color: 'error.main', display: 'inline-flex' }}>
                  <BugReportIcon size={32} />
                </Box>
                <Typography variant="h5" color="error">
                  {title || 'Something went wrong'}
                </Typography>
              </Box>

              <Alert severity="error" sx={{ mb: 2 }}>
                <AlertTitle>Application Error</AlertTitle>
                {message || 'An unexpected error occurred. Please try refreshing the page or contact support if the problem persists.'}
              </Alert>

              <Box display="flex" gap={2} mb={2}>
                <Button
                  variant="contained"
                  startIcon={<RefreshIcon />}
                  onClick={this.handleRetry}
                >
                  Try Again
                </Button>
                <Button
                  variant="outlined"
                  onClick={this.handleReload}
                >
                  Reload Page
                </Button>
              </Box>

              {/* Error details toggle */}
              <Box>
                <Button
                  variant="text"
                  size="small"
                  startIcon={this.state.showDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  onClick={this.toggleDetails}
                  sx={{ mb: 1 }}
                >
                  {this.state.showDetails ? 'Hide' : 'Show'} Error Details
                </Button>

                <Collapse in={this.state.showDetails}>
                  <Alert severity="info" sx={{ mt: 1 }}>
                    <AlertTitle>Technical Details</AlertTitle>
                    <Typography variant="body2" component="pre" sx={{ 
                      whiteSpace: 'pre-wrap', 
                      fontSize: '0.75rem',
                      fontFamily: 'monospace',
                      maxHeight: 200,
                      overflow: 'auto'
                    }}>
                      {this.state.error && this.state.error.toString()}
                      {this.state.errorInfo && this.state.errorInfo.componentStack}
                    </Typography>
                  </Alert>
                </Collapse>
              </Box>
            </CardContent>
          </Card>
        </Box>
      );
    }

    return this.props.children;
  }
}

/**
 * Higher-order component to wrap components with error boundary
 */
export const withErrorBoundary = (Component, errorBoundaryProps = {}) => {
  const WrappedComponent = (props) => (
    <ErrorBoundary {...errorBoundaryProps}>
      <Component {...props} />
    </ErrorBoundary>
  );
  
  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;
  return WrappedComponent;
};

/**
 * Lightweight error boundary for specific sections
 */
export const SectionErrorBoundary = ({ children, title }) => {
  return (
    <ErrorBoundary
      title={title}
      fallback={({ error, onRetry }) => (
        <Alert 
          severity="error" 
          action={
            <Button color="inherit" size="small" onClick={onRetry}>
              Retry
            </Button>
          }
        >
          <AlertTitle>{title || 'Section Error'}</AlertTitle>
          This section failed to load. {error?.message}
        </Alert>
      )}
    >
      {children}
    </ErrorBoundary>
  );
};

/**
 * Error boundary specifically for API operations
 */
export const ApiErrorBoundary = ({ children }) => {
  return (
    <ErrorBoundary
      title="Data Loading Error"
      message="Failed to load data from the server. Please check your connection and try again."
      fallback={({ onRetry, onReload }) => (
        <Box textAlign="center" p={3}>
          <Alert severity="error" sx={{ mb: 2 }}>
            <AlertTitle>Data Loading Error</AlertTitle>
            Failed to load data from the server. Please check your connection and try again.
          </Alert>
          <Box display="flex" justifyContent="center" gap={2}>
            <Button variant="contained" onClick={onRetry}>
              Retry
            </Button>
            <Button variant="outlined" onClick={onReload}>
              Reload Page
            </Button>
          </Box>
        </Box>
      )}
    >
      {children}
    </ErrorBoundary>
  );
};

export default ErrorBoundary;